<script context="module" lang="ts">
  import type { StorePinData } from './storeTypes';
  import type { Point } from './shapeUtils';
  import type { ShapeCategory } from './areaUtils';

  // Define the shape data structure here to make it exportable
  export type MappedShapeData = StorePinData & {
    shapeId: string;
    baseShapeId: string;
    relatedShapeIds: string[];
    shapeElement: SVGGraphicsElement;
    bbox: DOMRect;
    labelPosition: Point;
    area: number;
    areaCategory: ShapeCategory;
  };
</script>

<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';
  import type { StoreEventHandlers } from './storeTypes';
  import { debugLog, debugWarn } from './debugUtils';
  import { getLabelPosition } from './labelUtils';
  import { getPolygonArea, getShapeCategory } from './areaUtils';
  import { 
    findShapeAtPoint, 
    findStoreShapesContainer, 
    findRelatedShapes, 
    getBaseShapeId,
    getPolygonFromElement,
    getCombinedBoundingBox,
    highlightShape,
    unhighlightShape
  } from './shapeUtils';
  
  // Props
  export let svgElement: SVGSVGElement | null = null;
  export let pins: StorePinData[] = [];
  export let onStoreClick: (storeId: string) => void = () => {};
  export let onStoreHover: (storeId: string | null) => void = () => {};
  export let eventHandlers: Map<string, StoreEventHandlers>;
  export let highlightedStoreId: string | null = null;
  export let enableStoreClick: boolean = true;
  export let enableStoreHover: boolean = true;
  export let shapeHighlighting: any = {};
  export let dynamicLabelingConfig: any = {};
  
  const dispatch = createEventDispatcher();
  
  // Add event listeners to shapes
  function setupShapeForPin(pin: MappedShapeData) {
    if (!svgElement || !pin.shapeId || !pin.relatedShapeIds) return;

    pin.relatedShapeIds.forEach(shapeId => {
      const shape = svgElement!.querySelector(`#${shapeId}`) as SVGElement | HTMLElement;
      if (shape) {
        shape.classList.add('mapped-store-shape');
        shape.setAttribute('data-store-id', pin.id);
        shape.setAttribute('data-store-name', pin.name);
        shape.style.pointerEvents = 'auto';
        shape.style.cursor = 'pointer';
            
        if (!eventHandlers.has(shapeId)) {
          const handlers: StoreEventHandlers = {
            click: () => onStoreClick(pin.id),
            mouseenter: () => {
              if (pin.id !== highlightedStoreId) {
                highlightShape(svgElement, shapeId, pin, shapeHighlighting.fillColor, shapeHighlighting.strokeColor, shapeHighlighting.strokeWidth, shapeHighlighting.opacity);
              }
        onStoreHover(pin.id);
      },
            mouseleave: () => {
              if (pin.id !== highlightedStoreId) {
                unhighlightShape(svgElement, shapeId, pin);
              }
              onStoreHover(null);
              }
          };
          if (enableStoreClick) {
            shape.addEventListener('click', handlers.click as EventListener);
          }
          if (enableStoreHover) {
            shape.addEventListener('mouseenter', handlers.mouseenter as EventListener);
            shape.addEventListener('mouseleave', handlers.mouseleave as EventListener);
          }
          eventHandlers.set(shapeId, handlers);
        }
      }
    });
  }
  
  // Function to match pins with their underlying shapes
  export function findShapesForPins() {
    if (!svgElement || !pins) {
      debugWarn('Cannot find shapes: SVG element not found or no pins provided');
      return;
    }
    
    const mappedShapes: MappedShapeData[] = [];
    
    // Clean up any existing shape mappings to prevent stale data
    const existingMappedShapes = svgElement.querySelectorAll('.mapped-store-shape');
    existingMappedShapes.forEach(shape => {
      shape.classList.remove('mapped-store-shape');
      shape.removeAttribute('data-store-id');
      shape.removeAttribute('data-store-name');
    });
    
    pins.forEach(pin => {
      const shapeId = findShapeAtPoint(svgElement!, pin.x, pin.y);
      if (shapeId) {
        const baseShapeId = getBaseShapeId(shapeId);
        const relatedShapeIds = findRelatedShapes(svgElement!, baseShapeId).map(
          shape => shape.id
        );
        const finalShapeIds = relatedShapeIds.length > 0 ? relatedShapeIds : [shapeId];
        const shapeElements = finalShapeIds
          .map(id => svgElement!.querySelector(`#${id}`) as SVGGraphicsElement)
          .filter(el => el); // Filter out nulls

        if (shapeElements.length > 0) {
          let bbox: DOMRect;
          let polygon: [number, number][];
          const updatedPin = { ...pin };

          if (shapeElements.length > 1) {
            // Multipart store: use combined geometry
            bbox = getCombinedBoundingBox(shapeElements);
            polygon = [
              [bbox.x, bbox.y],
              [bbox.x + bbox.width, bbox.y],
              [bbox.x + bbox.width, bbox.y + bbox.height],
              [bbox.x, bbox.y + bbox.height]
            ];
            // Update pin to center of combined shape
            updatedPin.x = bbox.x + bbox.width / 2;
            updatedPin.y = bbox.y + bbox.height / 2;
          } else {
            // Single part store: use its own, more detailed geometry
            const shapeElement = shapeElements[0];
            bbox = shapeElement.getBBox();
            polygon = getPolygonFromElement(shapeElement);
      }

          const labelPosition = getLabelPosition(polygon as any);
          const area = getPolygonArea(polygon as any);
          const areaCategory = getShapeCategory(
            area, 
            dynamicLabelingConfig.smallAreaThreshold, 
            dynamicLabelingConfig.mediumAreaThreshold
          );

          const shapeData: MappedShapeData = {
            ...updatedPin,
            shapeId,
            baseShapeId,
            relatedShapeIds: finalShapeIds,
            shapeElement: shapeElements[0], // Keep a reference to the primary shape
            bbox, // Use the appropriate bbox
            labelPosition,
            area,
            areaCategory
          };
          mappedShapes.push(shapeData);

          setupShapeForPin(shapeData);
        }
      } else {
        debugLog(`No shape found for store "${pin.name}" (${pin.id}) at (${pin.x}, ${pin.y})`, 'shapeMatching');
  }
    });
    
    dispatch('shapesfound', { shapes: mappedShapes });

    debugLog(`Successfully matched ${mappedShapes.length} of ${pins.length} pins to shapes`, 'shapeMatching');
  }
  
  onMount(() => {
    if (svgElement && pins.length > 0) {
      findShapesForPins();
    }
  });
</script> 

<svelte:options accessors={true} /> 